import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const useStyles = makeStyles((theme) => ({
  benefit: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `3px dashed ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  benefitLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  textField: {
    width: '100%',
  },
  listItemIcons: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(1),
  },
  listItemIcon: {
    padding: theme.spacing(0.5),
  },
}));

const FAQEditor = ({ faq, onUpdate }) => {
  const classes = useStyles();
  const [localFAQ, setLocalFAQ] = useState(faq);

  // Sync with parent when faq prop changes
  useEffect(() => {
    setLocalFAQ(faq);
  }, [faq]);

  function handleUpdateFAQ(e, index) {
    const newFAQ = [...localFAQ];
    newFAQ[index][e.target.name] = e.target.value;
    setLocalFAQ(newFAQ);
  }

  function handleBlurFAQ() {
    onUpdate(localFAQ);
  }

  function handleAddFAQ() {
    const newFAQ = [
      ...localFAQ,
      {
        title: '',
        content: '',
      },
    ];
    setLocalFAQ(newFAQ);
    onUpdate(newFAQ);
  }

  function handleDeleteFAQ(index) {
    const newFAQ = localFAQ.filter((_, i) => i !== index);
    setLocalFAQ(newFAQ);
    onUpdate(newFAQ);
  }

  function handleDragEnd(result) {
    if (!result.destination) return;

    const reorderedFAQ = Array.from(localFAQ);
    const [movedItem] = reorderedFAQ.splice(result.source.index, 1);
    reorderedFAQ.splice(result.destination.index, 0, movedItem);

    setLocalFAQ(reorderedFAQ);
    onUpdate(reorderedFAQ);
  }

  if (!localFAQ.length) {
    return (
      <IconButton
        size='small'
        color='primary'
        aria-label='Add'
        component='span'
        onClick={() => handleAddFAQ()}
        className={classes.listItemIcon}
      >
        <AddIcon />
      </IconButton>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId='faq-list'>
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {localFAQ.map(({ title, content }, index) => (
              <Draggable key={`faq-${index}`} draggableId={`faq-${index}`} index={index}>
                {(provided) => (
                  <Box
                    className={classes.benefit}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <Box className={classes.benefitLeft}>
                      <TextField
                        name='title'
                        label='Question'
                        value={title}
                        onChange={(e) => handleUpdateFAQ(e, index)}
                        onBlur={handleBlurFAQ}
                        className={classes.textField}
                      />
                      <TextField
                        name='content'
                        label='Answer'
                        value={content}
                        multiline
                        maxRows={4}
                        onChange={(e) => handleUpdateFAQ(e, index)}
                        onBlur={handleBlurFAQ}
                        className={classes.textField}
                      />
                    </Box>
                    <Box className={classes.listItemIcons}>
                      <IconButton
                        color='secondary'
                        aria-label='delete faq'
                        component='span'
                        size='small'
                        className={classes.listItemIcon}
                        onClick={() => handleDeleteFAQ(index)}
                      >
                        <DeleteIcon />
                      </IconButton>

                      <IconButton
                        size='small'
                        color='primary'
                        aria-label='Add'
                        component='span'
                        onClick={() => handleAddFAQ()}
                        className={classes.listItemIcon}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

FAQEditor.propTypes = {
  faq: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    })
  ).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default React.memo(FAQEditor);
